import axios from 'axios';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';

const PIN_CODE = '76539';

export default function BackupScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  // PIN auth state
  const [authenticated, setAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinShake, setPinShake] = useState(false);

  // Backup state
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState('');

  const headers = {
    Authorization: `Bearer ${userInfo.token}`,
    'x-backup-pin': PIN_CODE,
  };

  // Verify PIN
  function handlePinSubmit(e) {
    e.preventDefault();
    if (pinInput === PIN_CODE) {
      setAuthenticated(true);
      setPinError('');
    } else {
      setPinError('Incorrect PIN. Please try again.');
      setPinShake(true);
      setPinInput('');
      setTimeout(() => setPinShake(false), 500);
    }
  }

  // Fetch backups
  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(api + '/api/backup/list?pin=' + PIN_CODE, { headers });
      setBackups(data.backups || []);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [userInfo.token]);

  useEffect(() => {
    if (authenticated) fetchBackups();
  }, [authenticated]);

  // Poll for a backup's status until it's no longer processing/restoring
  async function pollBackupStatus(backupId, actionLabel) {
    const maxAttempts = 120; // 10 minutes max (5s intervals)
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await axios.get(
          api + `/api/backup/status/${backupId}?pin=${PIN_CODE}`,
          { headers }
        );
        const status = data.backup?.status;
        if (status === 'completed') {
          toast.success(`${actionLabel} completed successfully!`);
          setProgress('');
          fetchBackups();
          return true;
        }
        if (status === 'failed') {
          toast.error(`${actionLabel} failed. Check the backup record for details.`);
          setProgress('');
          fetchBackups();
          return false;
        }
        // Still processing/restoring, continue polling
      } catch (err) {
        // Network blip, keep polling
      }
    }
    toast.error(`${actionLabel} timed out. Check the backup list for status.`);
    setProgress('');
    fetchBackups();
    return false;
  }

  // Create backup
  async function handleCreateBackup() {
    if (!window.confirm('Create a full database backup now? This may take a few minutes.')) return;
    try {
      setCreating(true);
      setProgress('Starting backup... Exporting database and uploading to Cloudinary...');
      const { data } = await axios.post(
        api + '/api/backup/create',
        { pin: PIN_CODE, note },
        { headers }
      );
      toast.info('Backup started. Processing in background...');
      setNote('');

      // Poll for completion
      if (data.backup?._id) {
        setProgress('Backup in progress... This may take a few minutes. Do not close this page.');
        await pollBackupStatus(data.backup._id, 'Backup');
      }
    } catch (err) {
      toast.error('Backup failed: ' + getError(err));
      setProgress('');
    } finally {
      setCreating(false);
    }
  }

  // Restore backup
  async function handleRestore(backup) {
    const confirmMsg = `WARNING: This will REPLACE all current data with the backup from:\n\n${formatDate(backup.createdAt)}\n${backup.collections} collections, ${backup.totalDocuments.toLocaleString()} documents\n\nThis cannot be undone. Are you sure?`;
    if (!window.confirm(confirmMsg)) return;
    if (!window.confirm('FINAL CONFIRMATION: All existing data will be overwritten. Continue?')) return;

    try {
      setRestoringId(backup._id);
      setProgress('Restore started... Downloading and restoring data...');
      const { data } = await axios.post(
        api + `/api/backup/restore/${backup._id}`,
        { pin: PIN_CODE },
        { headers }
      );
      toast.info('Restore started. Processing in background...');

      // Poll for completion
      setProgress('Restore in progress... This may take a few minutes. Do not close this page.');
      await pollBackupStatus(backup._id, 'Restore');
    } catch (err) {
      toast.error('Restore failed: ' + getError(err));
      setProgress('');
    } finally {
      setRestoringId(null);
    }
  }

  // Delete backup
  async function handleDelete(backup) {
    if (!window.confirm(`Delete backup from ${formatDate(backup.createdAt)}? This will also remove the file from Cloudinary.`)) return;
    try {
      setDeletingId(backup._id);
      const { data } = await axios.delete(api + `/api/backup/${backup._id}?pin=${PIN_CODE}`, { headers });
      toast.success(data.message);
      fetchBackups();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  function formatSize(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  // PIN Entry Screen
  if (!authenticated) {
    return (
      <div style={s.pageWrapper}>
        <Helmet><title>Database Backup</title></Helmet>
        <div style={s.pinScreen}>
          <div style={s.pinCard}>
            <div style={s.pinIconWrapper}>
              <i className="fa fa-lock" style={{ fontSize: 36, color: '#0d6efd' }} />
            </div>
            <h4 style={{ margin: '16px 0 6px', fontWeight: 700, color: '#1a1a2e' }}>Backup Access</h4>
            <p style={{ color: '#6c757d', fontSize: 14, margin: '0 0 24px' }}>
              Enter the security PIN to access database backup
            </p>
            <form onSubmit={handlePinSubmit}>
              <div style={{
                ...s.pinInputWrapper,
                animation: pinShake ? 'shake 0.5s ease' : 'none',
                borderColor: pinError ? '#dc3545' : '#dee2e6',
              }}>
                <input
                  type="password"
                  value={pinInput}
                  onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
                  placeholder="Enter PIN"
                  maxLength={10}
                  style={s.pinInput}
                  autoFocus
                />
              </div>
              {pinError && <p style={s.pinErrorText}>{pinError}</p>}
              <button
                type="submit"
                disabled={!pinInput}
                style={{
                  ...s.btn, ...s.btnPrimary,
                  width: '100%', justifyContent: 'center', marginTop: 16,
                  padding: '12px 20px', fontSize: 15,
                  opacity: !pinInput ? 0.6 : 1,
                }}
              >
                <i className="fa fa-unlock" /> Verify PIN
              </button>
            </form>
          </div>
        </div>
        <style>{shakeKeyframes}</style>
      </div>
    );
  }

  // Main Backup Screen
  return (
    <div style={s.pageWrapper}>
      <Helmet><title>Database Backup</title></Helmet>

      {/* Header */}
      <div style={s.pageHeader}>
        <div>
          <h4 style={s.pageTitle}>
            <i className="fa fa-database" style={{ marginRight: 10, color: '#0d6efd' }} />
            Database Backup Manager
          </h4>
          <p style={s.pageSubtitle}>Create, manage, and restore full database backups stored on Cloudinary</p>
        </div>
        <button
          onClick={() => { setAuthenticated(false); setPinInput(''); }}
          style={{ ...s.btn, ...s.btnOutline }}
        >
          <i className="fa fa-lock" /> Lock
        </button>
      </div>

      {/* Progress Banner */}
      {progress && (
        <div style={s.progressBanner}>
          <i className="fa fa-spinner fa-spin" style={{ marginRight: 10 }} />
          {progress}
        </div>
      )}

      {/* Create Backup Card */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h6 style={s.cardTitle}>
            <i className="fa fa-plus-circle" style={{ marginRight: 8, color: '#198754' }} />
            Create New Backup
          </h6>
        </div>
        <div style={s.cardBody}>
          <p style={{ color: '#6c757d', fontSize: 13, margin: '0 0 14px' }}>
            This will export all database collections as JSON and upload to Cloudinary in the <strong>clickcart-backups</strong> folder.
          </p>
          <div style={s.createRow}>
            <input
              type="text"
              placeholder="Optional note (e.g., Before migration, Weekly backup...)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={s.noteInput}
            />
            <button
              onClick={handleCreateBackup}
              disabled={creating}
              style={{
                ...s.btn, ...s.btnSuccess,
                opacity: creating ? 0.6 : 1,
                padding: '10px 24px',
              }}
            >
              {creating ? (
                <><i className="fa fa-spinner fa-spin" /> Creating Backup...</>
              ) : (
                <><i className="fa fa-cloud-upload" /> Create Backup Now</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Backup List */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <h6 style={s.cardTitle}>
            <i className="fa fa-history" style={{ marginRight: 8, color: '#0d6efd' }} />
            Backup History
          </h6>
          <button onClick={fetchBackups} disabled={loading} style={{ ...s.btn, ...s.btnOutlineSmall }}>
            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading && backups.length === 0 ? (
          <div style={s.centerState}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 28, color: '#0d6efd' }} />
            <p style={{ marginTop: 12, color: '#6c757d' }}>Loading backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div style={s.centerState}>
            <i className="fa fa-inbox" style={{ fontSize: 40, color: '#dee2e6' }} />
            <p style={{ marginTop: 12, color: '#6c757d' }}>No backups found. Create your first backup above.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div style={s.tableWrapper} className="backup-table-view">
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>#</th>
                    <th style={s.th}>Date & Time</th>
                    <th style={s.th}>Collections</th>
                    <th style={s.th}>Documents</th>
                    <th style={s.th}>Size</th>
                    <th style={s.th}>Note</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b, i) => (
                    <tr key={b._id} style={s.tr}>
                      <td style={{ ...s.td, color: '#adb5bd', fontWeight: 600 }}>{i + 1}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={s.dateIcon}>
                            <i className="fa fa-cloud" style={{ color: '#0d6efd' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>{formatDate(b.createdAt)}</div>
                            <div style={{ fontSize: 11, color: '#adb5bd', fontFamily: 'monospace' }}>{b.filename}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{b.collections}</td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{b.totalDocuments.toLocaleString()}</td>
                      <td style={s.td}>
                        <span style={s.sizeBadge}>{formatSize(b.sizeBytes)}</span>
                      </td>
                      <td style={{ ...s.td, fontSize: 13, color: '#6c757d', maxWidth: 200 }}>
                        {b.note || <span style={{ color: '#dee2e6' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        <span style={{
                          ...s.statusBadge,
                          background: b.status === 'completed' ? '#d1e7dd' : (b.status === 'restoring' || b.status === 'processing') ? '#cff4fc' : '#f8d7da',
                          color: b.status === 'completed' ? '#0f5132' : (b.status === 'restoring' || b.status === 'processing') ? '#055160' : '#842029',
                        }}>
                          <i className={`fa ${b.status === 'completed' ? 'fa-check-circle' : (b.status === 'restoring' || b.status === 'processing') ? 'fa-spinner fa-spin' : 'fa-times-circle'}`} style={{ marginRight: 4 }} />
                          {b.status === 'completed' ? 'Completed' : b.status === 'processing' ? 'Processing...' : b.status === 'restoring' ? 'Restoring...' : 'Failed'}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={s.actionBtns}>
                          <a
                            href={b.cloudinaryUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={s.actionBtn}
                            title="Download backup"
                          >
                            <i className="fa fa-download" />
                          </a>
                          <button
                            onClick={() => handleRestore(b)}
                            disabled={restoringId === b._id}
                            style={{ ...s.actionBtn, color: '#fd7e14', borderColor: '#ffe5cc' }}
                            title="Restore from this backup"
                          >
                            {restoringId === b._id ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-undo" />}
                          </button>
                          <button
                            onClick={() => handleDelete(b)}
                            disabled={deletingId === b._id}
                            style={{ ...s.actionBtn, color: '#dc3545', borderColor: '#f5c2c7' }}
                            title="Delete this backup"
                          >
                            {deletingId === b._id ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-trash" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="backup-card-view" style={{ display: 'none', padding: '0 16px 16px' }}>
              {backups.map((b, i) => (
                <div key={b._id} style={s.mobileCard}>
                  <div style={s.mobileCardHead}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={s.dateIcon}>
                        <i className="fa fa-cloud" style={{ color: '#0d6efd' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>{formatDate(b.createdAt)}</div>
                        <div style={{ fontSize: 11, color: '#adb5bd' }}>#{i + 1}</div>
                      </div>
                    </div>
                    <span style={{
                      ...s.statusBadge,
                      background: b.status === 'completed' ? '#d1e7dd' : '#f8d7da',
                      color: b.status === 'completed' ? '#0f5132' : '#842029',
                      fontSize: 11,
                    }}>
                      {b.status === 'completed' ? 'Completed' : 'Failed'}
                    </span>
                  </div>
                  <div style={s.mobileStatGrid}>
                    <div>
                      <div style={s.mobileStatLabel}>Collections</div>
                      <div style={s.mobileStatVal}>{b.collections}</div>
                    </div>
                    <div>
                      <div style={s.mobileStatLabel}>Documents</div>
                      <div style={s.mobileStatVal}>{b.totalDocuments.toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={s.mobileStatLabel}>Size</div>
                      <div style={s.mobileStatVal}>{formatSize(b.sizeBytes)}</div>
                    </div>
                  </div>
                  {b.note && <div style={{ fontSize: 13, color: '#6c757d', marginTop: 10 }}>{b.note}</div>}
                  <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                    <a href={b.cloudinaryUrl} target="_blank" rel="noreferrer"
                      style={{ ...s.btn, ...s.btnOutlineSmall, flex: 1, justifyContent: 'center' }}>
                      <i className="fa fa-download" /> Download
                    </a>
                    <button onClick={() => handleRestore(b)} disabled={restoringId === b._id}
                      style={{ ...s.btn, ...s.btnWarningSmall, flex: 1, justifyContent: 'center' }}>
                      <i className="fa fa-undo" /> Restore
                    </button>
                    <button onClick={() => handleDelete(b)} disabled={deletingId === b._id}
                      style={{ ...s.btn, ...s.btnDangerSmall, justifyContent: 'center' }}>
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Info Card */}
      <div style={s.infoCard}>
        <div style={{ display: 'flex', gap: 12 }}>
          <i className="fa fa-info-circle" style={{ fontSize: 18, color: '#0d6efd', marginTop: 2 }} />
          <div style={{ fontSize: 13, color: '#495057', lineHeight: 1.6 }}>
            <strong>How it works:</strong><br />
            <strong>Backup</strong> exports all database collections as JSON and uploads to Cloudinary's <code>clickcart-backups</code> folder.<br />
            <strong>Restore</strong> downloads the backup and replaces all data (except backup records themselves).<br />
            <strong>Download</strong> lets you save the JSON backup file to your computer.<br />
            Always create a new backup before restoring an old one.
          </div>
        </div>
      </div>

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .backup-table-view { display: none !important; }
          .backup-card-view { display: block !important; }
        }
        ${shakeKeyframes}
      `}</style>
    </div>
  );
}

const shakeKeyframes = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-10px); }
    40% { transform: translateX(10px); }
    60% { transform: translateX(-6px); }
    80% { transform: translateX(6px); }
  }
`;

const s = {
  pageWrapper: {
    padding: '20px',
    maxWidth: 1200,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // PIN screen
  pinScreen: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  pinCard: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e9ecef',
    padding: '40px 36px',
    maxWidth: 380,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  pinIconWrapper: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: '#e8f4fd',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinInputWrapper: {
    border: '2px solid #dee2e6',
    borderRadius: 10,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  pinInput: {
    border: 'none',
    padding: '14px 18px',
    fontSize: 20,
    textAlign: 'center',
    letterSpacing: 8,
    fontWeight: 700,
    width: '100%',
    outline: 'none',
    color: '#1a1a2e',
  },
  pinErrorText: {
    color: '#dc3545',
    fontSize: 13,
    marginTop: 8,
    fontWeight: 500,
  },

  // Page header
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: { margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1a2e' },
  pageSubtitle: { margin: '4px 0 0', fontSize: 14, color: '#6c757d' },

  // Progress
  progressBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    background: '#cff4fc',
    borderRadius: 10,
    marginBottom: 16,
    border: '1px solid #9eeaf9',
    fontSize: 14,
    fontWeight: 600,
    color: '#055160',
  },

  // Card
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e9ecef',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    marginBottom: 16,
  },
  cardHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  cardTitle: { margin: 0, fontSize: 15, fontWeight: 600, color: '#1a1a2e' },
  cardBody: { padding: '16px 20px' },

  // Create row
  createRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  noteInput: {
    flex: 1,
    minWidth: 200,
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    outline: 'none',
    color: '#495057',
  },

  // Table
  tableWrapper: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600,
    color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '2px solid #e9ecef', background: '#fafbfc', whiteSpace: 'nowrap',
  },
  td: { padding: '12px 16px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'middle' },
  tr: { transition: 'background 0.15s' },

  dateIcon: {
    width: 38, height: 38, borderRadius: 10, background: '#e8f4fd',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0,
  },
  sizeBadge: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 6,
    background: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#495057', fontFamily: 'monospace',
  },
  statusBadge: {
    display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
    borderRadius: 20, fontSize: 12, fontWeight: 600,
  },

  // Actions
  actionBtns: { display: 'flex', gap: 4 },
  actionBtn: {
    width: 32, height: 32, borderRadius: 6, border: '1px solid #dee2e6',
    background: '#fff', color: '#495057', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, transition: 'all 0.15s', textDecoration: 'none',
  },

  // Mobile
  mobileCard: {
    border: '1px solid #e9ecef', borderRadius: 12, padding: 16, marginBottom: 12,
  },
  mobileCardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 14, flexWrap: 'wrap', gap: 8,
  },
  mobileStatGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  mobileStatLabel: {
    fontSize: 11, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  mobileStatVal: { fontSize: 16, fontWeight: 600, color: '#1a1a2e' },

  // Info card
  infoCard: {
    background: '#f0f7ff',
    borderRadius: 12,
    border: '1px solid #b6d4fe',
    padding: '16px 20px',
    marginBottom: 16,
  },

  centerState: { textAlign: 'center', padding: '50px 20px' },

  // Buttons
  btn: {
    padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13,
    fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', gap: 6, textDecoration: 'none',
    whiteSpace: 'nowrap', transition: 'all 0.15s',
  },
  btnPrimary: { background: '#0d6efd', color: '#fff' },
  btnSuccess: { background: '#198754', color: '#fff' },
  btnOutline: { background: 'transparent', color: '#6c757d', border: '1px solid #dee2e6' },
  btnOutlineSmall: {
    background: 'transparent', color: '#0d6efd', border: '1px solid #0d6efd',
    padding: '5px 12px', fontSize: 12,
  },
  btnWarningSmall: {
    background: '#fff3cd', color: '#664d03', border: '1px solid #ffc107',
    padding: '5px 12px', fontSize: 12,
  },
  btnDangerSmall: {
    background: '#f8d7da', color: '#842029', border: '1px solid #f5c2c7',
    padding: '5px 12px', fontSize: 12,
  },
};
